import {
  MappingCommissioningViewSchema,
  type MappingCommissioningConnectionBreakdown,
  type MappingCommissioningConnectionType,
  type MappingCommissioningControllerGroup,
  type MappingCommissioningRowKind,
  type MappingCommissioningScheduleRow,
  type MappingCommissioningTaskRow,
  type MappingCommissioningView,
  type SourceRef
} from "@automatry/schemas";

import { humaniseIdentifierText, labelFromId, solutionDisplayName } from "../app/format";
import type {
  ProjectFixtureCommissioningTask,
  ProjectFixtureForDisplay,
  ProjectFixtureMappingScheduleRow,
  ProjectFixtureOutputDeviceInstance,
  ProjectFixtureOutputGraph
} from "./projectCockpitAdapter";

type DeviceMappingType =
  | "direct_wired"
  | "network_pairing"
  | "netmaster_point_mapping"
  | "central_io"
  | "not_terminated_spare";
type PhysicalMappingKind =
  | "logical_only"
  | "physical_termination"
  | "central_io"
  | "network_pairing"
  | "not_terminated_spare";

interface ProjectFixtureDeviceMapping {
  id: string;
  deviceRef: string;
  logicalControllerRef?: string;
  physicalTerminationRef?: string;
  roomId?: string;
  zoneId?: string;
  mappingType: DeviceMappingType;
  cableImplication?: string;
  commissioningNote: string;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
  risk?: {
    riskRefs: string[];
  };
}

interface ProjectFixturePhysicalMapping {
  id: string;
  logicalOwnerRef: string;
  physicalTerminationRef?: string;
  roomId?: string;
  zoneId?: string;
  mappingKind: PhysicalMappingKind;
  cableImplication?: string;
  commissioningNote: string;
  sourceRefs: SourceRef[];
  ruleTraceRefs: string[];
  risk?: {
    riskRefs: string[];
  };
}

interface ProjectFixtureSolutionMappings {
  apartmentTypeId: string;
  mappings: ProjectFixtureDeviceMapping[];
  physicalMappings: ProjectFixturePhysicalMapping[];
}

interface MappingLookupValue {
  apartmentTypeId: string;
  rowKind: MappingCommissioningRowKind;
  mapping: ProjectFixtureDeviceMapping | ProjectFixturePhysicalMapping;
}

interface ApartmentTypeLabelMeta {
  rawLabel: string;
  beds?: number;
}

export interface MappingScheduleFilters {
  query?: string;
  apartmentTypeId?: string;
  controllerRef?: string;
  controllerRefs?: readonly string[];
  connectionType?: MappingCommissioningConnectionType | "all";
  rowKind?: MappingCommissioningRowKind | "all";
  spareOnly?: boolean;
}

export interface MappingCommissioningDisplayViewModel extends MappingCommissioningView {
  generatedFrom: string;
}

const mappingCommissioningGeneratedFromLabel =
  "Generated from the project's engine output";

const labelise = (value: string): string =>
  value
    .replace(/^carrington-/, "")
    .replace(/^type-/, "Type ")
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());

const humaniseMappingText = (
  value: string,
  devicesByRef: ReadonlyMap<string, ProjectFixtureOutputDeviceInstance>
): string =>
  humaniseIdentifierText(
    value.replace(
      /\b(?:instance-controller-)?controller-[A-Za-z0-9_-]+\b/g,
      (controllerRef) =>
        devicesByRef.get(controllerRef)?.label ??
        devicesByRef.get(controllerRef.replace(/^instance-controller-/, ""))?.label ??
        labelise(controllerRef)
    )
  )
    .replace(/\bWUFH\b/gi, "wet underfloor heating")
    .replace(/\bEUFH\b/gi, "electric underfloor heating")
    .replace(/\bUFH\b/gi, "underfloor heating")
    .replace(/\bfcu\b/gi, "FCU")
    .replace(/\bio\b/gi, "I/O")
    .replace(
      /\bTP-UI-TS-7 central control screen(?:\s+TS-7|\s+ts7)\b/gi,
      "TP-UI-TS-7 Network Screen"
    )
    .replace(/\bcentral control screen(?:\s+TS-7|\s+ts7)\b/gi, "TS-7 Network Screen")
    .replace(/\bts[-_ ]?4\b/gi, "TS-4")
    .replace(/\bts[-_ ]?7\b/gi, "TS-7")
    .replace(/\s+/g, " ")
    .trim();

const zeroBreakdown = (): MappingCommissioningConnectionBreakdown => ({
  directWired: 0,
  networkPairing: 0,
  centralIo: 0,
  notTerminatedSpare: 0,
  physicalTermination: 0
});

const incrementBreakdown = (
  breakdown: MappingCommissioningConnectionBreakdown,
  type: MappingCommissioningConnectionType
): void => {
  if (type === "direct_wired") {
    breakdown.directWired += 1;
  } else if (type === "network_pairing") {
    breakdown.networkPairing += 1;
  } else if (type === "central_io") {
    breakdown.centralIo += 1;
  } else if (type === "not_terminated_spare") {
    breakdown.notTerminatedSpare += 1;
  } else if (type === "physical_termination") {
    breakdown.physicalTermination += 1;
  }
};

const connectionTypeFromMapping = (
  value: DeviceMappingType | PhysicalMappingKind
): MappingCommissioningConnectionType => {
  if (value === "logical_only" || value === "netmaster_point_mapping") {
    return "physical_termination";
  }

  return value;
};

export const connectionTypeLabel = (
  type: MappingCommissioningConnectionType
): string => {
  if (type === "direct_wired") {
    return "Direct-wired";
  }

  if (type === "network_pairing") {
    return "Network-pairing";
  }

  if (type === "central_io") {
    return "Central-IO";
  }

  if (type === "not_terminated_spare") {
    return "Spare (not terminated)";
  }

  return "Physical termination";
};

export const mappingRowKindLabel = (kind: MappingCommissioningRowKind): string =>
  kind === "device_mapping" ? "Device mapping" : "Physical mapping";

const sourceMappingId = (row: ProjectFixtureMappingScheduleRow): string =>
  row.id.replace(/^mapping-row-/, "");

const deviceLabelMap = (
  graph: ProjectFixtureOutputGraph
): ReadonlyMap<string, ProjectFixtureOutputDeviceInstance> => {
  const entries = graph.deviceInstances.flatMap((device) => [
    [device.id, device] as const,
    [device.specItemRef, device] as const
  ]);

  return new Map(entries);
};

const refLabel = (
  graph: ProjectFixtureOutputGraph,
  devicesByRef: ReadonlyMap<string, ProjectFixtureOutputDeviceInstance>,
  ref: string | undefined
): string | undefined => {
  if (!ref) {
    return undefined;
  }

  const device = devicesByRef.get(ref);

  if (device) {
    return device.label;
  }

  if (graph.productQuantityTotals[ref] !== undefined) {
    return labelise(ref);
  }

  return labelise(ref);
};

const apartmentTypeLabelMeta = (
  fixture: ProjectFixtureForDisplay
): ReadonlyMap<string, ApartmentTypeLabelMeta> =>
  new Map(
    fixture.takeoffRows.map((row) => [
      row.apartmentTypeId,
      {
        rawLabel: row.rawLabel,
        ...(row.beds !== undefined ? { beds: row.beds } : {})
      }
    ])
  );

const apartmentTypeLabel = (
  apartmentTypeId: string,
  projectId: string,
  metaByApartmentTypeId: ReadonlyMap<string, ApartmentTypeLabelMeta>
): string => {
  const meta = metaByApartmentTypeId.get(apartmentTypeId);

  return solutionDisplayName(apartmentTypeId, {
    projectId,
    ...(meta ? { rawLabel: meta.rawLabel } : {}),
    ...(meta?.beds !== undefined ? { beds: meta.beds } : {})
  });
};

const zoneKindLabels: Record<string, string> = {
  eufh: "underfloor heating (electric)",
  fcu: "fan coil",
  towel_rail_load_relay_output: "towel-rail relay output",
  wufh: "underfloor heating (wet)"
};

const zoneLabelPartsFromId = (
  zoneId: string
): { apartmentTypeId: string; kindLabel: string; number?: string } | null => {
  const parts = zoneId.replace(/^zone-/, "").split("-");
  const lastPart = parts[parts.length - 1];
  const hasNumber = lastPart !== undefined && /^\d+$/.test(lastPart);
  const kindIndex = hasNumber ? parts.length - 2 : parts.length - 1;
  const kind = parts[kindIndex];

  if (!kind || !zoneKindLabels[kind]) {
    return null;
  }

  const apartmentTypeId = parts.slice(0, kindIndex).join("-");

  if (!apartmentTypeId) {
    return null;
  }

  return {
    apartmentTypeId,
    kindLabel: zoneKindLabels[kind],
    ...(hasNumber ? { number: lastPart } : {})
  };
};

const zoneLabelFromId = (
  zoneId: string,
  projectId: string,
  fallbackApartmentTypeId: string,
  metaByApartmentTypeId: ReadonlyMap<string, ApartmentTypeLabelMeta>,
  options: { includeApartmentType: boolean }
): string => {
  const parts = zoneLabelPartsFromId(zoneId);

  if (!parts) {
    return labelFromId(zoneId.replace(/^zone-/, ""), projectId);
  }

  const apartmentLabel = apartmentTypeLabel(
    fallbackApartmentTypeId || parts.apartmentTypeId,
    projectId,
    metaByApartmentTypeId
  );
  const zoneLabel = `${parts.kindLabel} zone${parts.number ? ` ${parts.number}` : ""}`;

  return options.includeApartmentType ? `${apartmentLabel} · ${zoneLabel}` : zoneLabel;
};

const roomLabelFromMappingRoomId = (
  roomId: string,
  zoneId: string | undefined,
  projectId: string,
  apartmentTypeId: string,
  metaByApartmentTypeId: ReadonlyMap<string, ApartmentTypeLabelMeta>
): string =>
  roomId === zoneId
    ? zoneLabelFromId(roomId, projectId, apartmentTypeId, metaByApartmentTypeId, {
        includeApartmentType: false
      })
    : labelise(roomId);

const buildMappingLookup = (
  fixture: ProjectFixtureForDisplay
): ReadonlyMap<string, MappingLookupValue> => {
  const lookup = new Map<string, MappingLookupValue>();
  const solutions =
    fixture.apartmentSolutions as unknown as readonly ProjectFixtureSolutionMappings[];

  for (const solution of solutions) {
    for (const mapping of solution.mappings) {
      lookup.set(mapping.id, {
        apartmentTypeId: solution.apartmentTypeId,
        rowKind: "device_mapping",
        mapping
      });
    }

    for (const mapping of solution.physicalMappings) {
      lookup.set(mapping.id, {
        apartmentTypeId: solution.apartmentTypeId,
        rowKind: "physical_mapping",
        mapping
      });
    }
  }

  return lookup;
};

const rowFromSchedule = ({
  projectId,
  metaByApartmentTypeId,
  graph,
  devicesByRef,
  scheduleRow,
  lookup
}: {
  projectId: string;
  metaByApartmentTypeId: ReadonlyMap<string, ApartmentTypeLabelMeta>;
  graph: ProjectFixtureOutputGraph;
  devicesByRef: ReadonlyMap<string, ProjectFixtureOutputDeviceInstance>;
  scheduleRow: ProjectFixtureMappingScheduleRow;
  lookup: MappingLookupValue;
}): MappingCommissioningScheduleRow => {
  const isDeviceMapping = lookup.rowKind === "device_mapping";
  const deviceMapping = isDeviceMapping
    ? (lookup.mapping as ProjectFixtureDeviceMapping)
    : null;
  const physicalMapping = isDeviceMapping
    ? null
    : (lookup.mapping as ProjectFixturePhysicalMapping);
  const connectionType = connectionTypeFromMapping(
    deviceMapping?.mappingType ?? physicalMapping?.mappingKind ?? "physical_termination"
  );
  const controllerRef =
    scheduleRow.logicalControllerRef ??
    deviceMapping?.logicalControllerRef ??
    physicalMapping?.logicalOwnerRef;
  const terminationRef =
    scheduleRow.physicalTerminationRef ??
    deviceMapping?.physicalTerminationRef ??
    physicalMapping?.physicalTerminationRef;
  const zoneId = deviceMapping?.zoneId ?? physicalMapping?.zoneId;
  const apartmentTypeId = scheduleRow.apartmentTypeId ?? lookup.apartmentTypeId;
  const fallbackRoomLabel = deviceMapping?.roomId
    ? roomLabelFromMappingRoomId(
        deviceMapping.roomId,
        zoneId,
        projectId,
        apartmentTypeId,
        metaByApartmentTypeId
      )
    : physicalMapping?.roomId
      ? roomLabelFromMappingRoomId(
          physicalMapping.roomId,
          zoneId,
          projectId,
          apartmentTypeId,
          metaByApartmentTypeId
        )
      : undefined;

  return {
    id: scheduleRow.id,
    sourceMappingId: sourceMappingId(scheduleRow),
    apartmentTypeId,
    apartmentTypeLabel: apartmentTypeLabel(
      apartmentTypeId,
      projectId,
      metaByApartmentTypeId
    ),
    rowKind: lookup.rowKind,
    connectionType,
    connectionLabel: connectionTypeLabel(connectionType),
    deviceRef:
      deviceMapping?.deviceRef ??
      physicalMapping?.logicalOwnerRef ??
      scheduleRow.deviceInstanceId,
    deviceLabel: humaniseMappingText(
      refLabel(graph, devicesByRef, scheduleRow.deviceInstanceId) ??
        labelise(scheduleRow.deviceInstanceId),
      devicesByRef
    ),
    ...(controllerRef
      ? {
          controllerRef,
          controllerLabel: humaniseMappingText(
            refLabel(graph, devicesByRef, controllerRef) ?? labelise(controllerRef),
            devicesByRef
          )
        }
      : {}),
    ...(terminationRef
      ? {
          terminationRef,
          terminationLabel: humaniseMappingText(
            refLabel(graph, devicesByRef, terminationRef) ?? labelise(terminationRef),
            devicesByRef
          )
        }
      : {}),
    roomLabel: humaniseMappingText(
      scheduleRow.room ?? fallbackRoomLabel ?? "Room not produced",
      devicesByRef
    ),
    ...(zoneId
      ? {
          zoneLabel: humaniseMappingText(
            zoneLabelFromId(zoneId, projectId, apartmentTypeId, metaByApartmentTypeId, {
              includeApartmentType: true
            }),
            devicesByRef
          )
        }
      : {}),
    ...(scheduleRow.cableImplication
      ? {
          cableImplication: humaniseMappingText(
            scheduleRow.cableImplication,
            devicesByRef
          )
        }
      : {}),
    commissioningNote: humaniseMappingText(scheduleRow.commissioningNote, devicesByRef),
    riskRefs: scheduleRow.riskRefs,
    sourceRefCount: scheduleRow.sourceRefs.length,
    sourceRefs: scheduleRow.sourceRefs,
    ruleTraceRefs: scheduleRow.ruleTraceRefs,
    isSpare: connectionType === "not_terminated_spare"
  };
};

const mappingRows = (
  fixture: ProjectFixtureForDisplay
): {
  rows: MappingCommissioningScheduleRow[];
  unmatchedRowCount: number;
  deviceConnectionBreakdown: MappingCommissioningConnectionBreakdown;
} => {
  const lookup = buildMappingLookup(fixture);
  const projectId = fixture.project.projectId;
  const metaByApartmentTypeId = apartmentTypeLabelMeta(fixture);
  const rows: MappingCommissioningScheduleRow[] = [];
  const deviceConnectionBreakdown = zeroBreakdown();
  let unmatchedRowCount = 0;

  for (const value of lookup.values()) {
    if (value.rowKind === "device_mapping") {
      incrementBreakdown(
        deviceConnectionBreakdown,
        connectionTypeFromMapping(
          (value.mapping as ProjectFixtureDeviceMapping).mappingType
        )
      );
    }
  }

  for (const graph of fixture.outputBundle.outputGraphs) {
    const devicesByRef = deviceLabelMap(graph);

    for (const scheduleRow of graph.mappingScheduleRows) {
      const sourceId = sourceMappingId(scheduleRow);
      const match = lookup.get(sourceId);

      if (!match) {
        unmatchedRowCount += 1;
        continue;
      }

      rows.push(
        rowFromSchedule({
          projectId,
          metaByApartmentTypeId,
          graph,
          devicesByRef,
          scheduleRow,
          lookup: match
        })
      );
    }
  }

  return {
    rows,
    unmatchedRowCount,
    deviceConnectionBreakdown
  };
};

const commissioningTasks = (
  fixture: ProjectFixtureForDisplay
): MappingCommissioningTaskRow[] => {
  const projectId = fixture.project.projectId;
  const metaByApartmentTypeId = apartmentTypeLabelMeta(fixture);

  return fixture.outputBundle.outputGraphs.flatMap((graph) => {
    const devicesByRef = deviceLabelMap(graph);
    const graphApartmentTypeId =
      graph.mappingScheduleRows.find((row) => row.apartmentTypeId)?.apartmentTypeId ??
      graph.deviceInstances.find((device) => device.apartmentTypeId)?.apartmentTypeId ??
      "project";

    return graph.commissioningTasks.map(
      (task: ProjectFixtureCommissioningTask): MappingCommissioningTaskRow => {
        const taskApartmentTypeId =
          task.deviceInstanceIds
            .map((id) => devicesByRef.get(id)?.apartmentTypeId)
            .find((id): id is string => id !== undefined) ?? graphApartmentTypeId;

        return {
          id: task.id,
          apartmentTypeId: taskApartmentTypeId,
          apartmentTypeLabel: apartmentTypeLabel(
            taskApartmentTypeId,
            projectId,
            metaByApartmentTypeId
          ),
          title: humaniseMappingText(task.title, devicesByRef),
          description: humaniseMappingText(task.description, devicesByRef),
          status: task.status,
          deviceLabels: task.deviceInstanceIds.map((id) =>
            humaniseMappingText(
              refLabel(graph, devicesByRef, id) ?? labelise(id),
              devicesByRef
            )
          ),
          riskRefs: task.riskRefs,
          sourceRefCount: task.sourceRefs.length,
          sourceRefs: task.sourceRefs,
          ruleTraceRefs: task.ruleTraceRefs
        };
      }
    );
  });
};

const scheduleConnectionBreakdown = (
  rows: readonly MappingCommissioningScheduleRow[]
): MappingCommissioningConnectionBreakdown => {
  const breakdown = zeroBreakdown();

  for (const row of rows) {
    incrementBreakdown(breakdown, row.connectionType);
  }

  return breakdown;
};

const apartmentSummaries = (
  fixture: ProjectFixtureForDisplay,
  rows: readonly MappingCommissioningScheduleRow[]
): MappingCommissioningView["apartmentSummaries"] => {
  const metaByApartmentTypeId = apartmentTypeLabelMeta(fixture);

  return fixture.takeoffRows.map((takeoff) => {
    const apartmentRows = rows.filter(
      (row) => row.apartmentTypeId === takeoff.apartmentTypeId
    );

    return {
      apartmentTypeId: takeoff.apartmentTypeId,
      apartmentTypeLabel: apartmentTypeLabel(
        takeoff.apartmentTypeId,
        fixture.project.projectId,
        metaByApartmentTypeId
      ),
      scheduleRowCount: apartmentRows.length,
      deviceMappingCount: apartmentRows.filter(
        (row) => row.rowKind === "device_mapping"
      ).length,
      physicalMappingCount: apartmentRows.filter(
        (row) => row.rowKind === "physical_mapping"
      ).length,
      sparePointCount: apartmentRows.filter((row) => row.isSpare).length
    };
  });
};

const controllerGroups = (
  rows: readonly MappingCommissioningScheduleRow[]
): MappingCommissioningControllerGroup[] => {
  const grouped = new Map<string, MappingCommissioningControllerGroup>();

  for (const row of rows) {
    if (!row.controllerRef || !row.controllerLabel) {
      continue;
    }

    const id = `${row.apartmentTypeId}|${row.controllerRef}`;
    const existing = grouped.get(id) ?? {
      id,
      apartmentTypeId: row.apartmentTypeId,
      apartmentTypeLabel: row.apartmentTypeLabel,
      controllerRef: row.controllerRef,
      controllerLabel: row.controllerLabel,
      scheduleRowCount: 0,
      deviceRowCount: 0,
      physicalRowCount: 0,
      sparePointCount: 0,
      connectionBreakdown: zeroBreakdown()
    };

    existing.scheduleRowCount += 1;
    existing.deviceRowCount += row.rowKind === "device_mapping" ? 1 : 0;
    existing.physicalRowCount += row.rowKind === "physical_mapping" ? 1 : 0;
    existing.sparePointCount += row.isSpare ? 1 : 0;
    incrementBreakdown(existing.connectionBreakdown, row.connectionType);
    grouped.set(id, existing);
  }

  return [...grouped.values()].sort((left, right) => {
    const typeDelta = left.apartmentTypeLabel.localeCompare(
      right.apartmentTypeLabel,
      "en-GB"
    );

    if (typeDelta !== 0) {
      return typeDelta;
    }

    return right.scheduleRowCount - left.scheduleRowCount;
  });
};

const dataGapsFor = (input: {
  rowCount: number;
  taskCount: number;
  unmatchedRowCount: number;
}): string[] => [
  ...(input.rowCount === 0
    ? ["The mapping schedule has not been produced for this project yet."]
    : []),
  ...(input.taskCount === 0
    ? ["The commissioning checklist has not been produced for this project yet."]
    : []),
  ...(input.unmatchedRowCount > 0
    ? [
        `${input.unmatchedRowCount} schedule row(s) could not be matched to a device or terminal row.`
      ]
    : []),
  "Per-controller usage is derived from rows that include a controller; terminal block numbering appears where the source data supplied a terminal reference.",
  "Room layout geometry is not available yet; room labels come from the schedule row or zone name."
];

export const projectFixtureToMappingCommissioningView = (
  fixture: ProjectFixtureForDisplay
): MappingCommissioningDisplayViewModel => {
  const { rows, unmatchedRowCount, deviceConnectionBreakdown } = mappingRows(fixture);
  const tasks = commissioningTasks(fixture);
  const physicalMappingCount = rows.filter(
    (row) => row.rowKind === "physical_mapping"
  ).length;
  const deviceMappingCount = rows.filter(
    (row) => row.rowKind === "device_mapping"
  ).length;
  const sparePointCount = rows.filter((row) => row.isSpare).length;
  const core = MappingCommissioningViewSchema.parse({
    projectId: fixture.project.projectId,
    status: fixture.outputBundle.status,
    scheduleRowIds: rows.map((row) => row.id),
    commissioningTaskIds: tasks.map((task) => task.id),
    updatedAt: fixture.fixture.generatedAt,
    stats: {
      scheduleRowCount: rows.length,
      deviceMappingCount,
      physicalMappingCount,
      sparePointCount,
      commissioningTaskCount: tasks.length,
      deviceConnectionBreakdown,
      scheduleConnectionBreakdown: scheduleConnectionBreakdown(rows)
    },
    rows,
    commissioningTasks: tasks,
    apartmentSummaries: apartmentSummaries(fixture, rows),
    controllerGroups: controllerGroups(rows),
    dataGaps: dataGapsFor({
      rowCount: rows.length,
      taskCount: tasks.length,
      unmatchedRowCount
    }),
    generatedFrom: mappingCommissioningGeneratedFromLabel
  });

  return {
    ...core,
    generatedFrom: mappingCommissioningGeneratedFromLabel
  };
};

const rowSearchText = (row: MappingCommissioningScheduleRow): string =>
  [
    row.id,
    row.apartmentTypeLabel,
    row.rowKind,
    row.connectionLabel,
    row.deviceLabel,
    row.controllerLabel,
    row.terminationLabel,
    row.roomLabel,
    row.zoneLabel,
    row.cableImplication,
    row.commissioningNote,
    row.riskRefs.join(" "),
    row.ruleTraceRefs.join(" ")
  ]
    .filter((value): value is string => typeof value === "string")
    .join(" ")
    .toLowerCase();

export const filterMappingScheduleRows = (
  rows: readonly MappingCommissioningScheduleRow[],
  filters: MappingScheduleFilters = {}
): MappingCommissioningScheduleRow[] => {
  const query = filters.query?.trim().toLowerCase() ?? "";

  return rows.filter((row) => {
    const matchesQuery = query ? rowSearchText(row).includes(query) : true;
    const matchesApartment =
      !filters.apartmentTypeId ||
      filters.apartmentTypeId === "all" ||
      row.apartmentTypeId === filters.apartmentTypeId;
    const controllerRefs = filters.controllerRefs;
    const matchesController =
      controllerRefs && controllerRefs.length > 0
        ? row.controllerRef !== undefined && controllerRefs.includes(row.controllerRef)
        : !filters.controllerRef ||
          filters.controllerRef === "all" ||
          row.controllerRef === filters.controllerRef;
    const matchesConnection =
      !filters.connectionType ||
      filters.connectionType === "all" ||
      row.connectionType === filters.connectionType;
    const matchesKind =
      !filters.rowKind || filters.rowKind === "all" || row.rowKind === filters.rowKind;
    const matchesSpare = filters.spareOnly ? row.isSpare : true;

    return (
      matchesQuery &&
      matchesApartment &&
      matchesController &&
      matchesConnection &&
      matchesKind &&
      matchesSpare
    );
  });
};

const csvCell = (value: string | number | boolean): string =>
  `"${String(value).replace(/"/g, '""')}"`;

export const buildMappingScheduleCsv = (
  rows: readonly MappingCommissioningScheduleRow[]
): string =>
  [
    [
      "Row id",
      "Apartment type",
      "Schedule row kind",
      "Connection type",
      "Device",
      "Room",
      "Zone",
      "Controller",
      "Termination",
      "Spare point",
      "Cable implication",
      "Commissioning note",
      "Risk refs",
      "Source refs",
      "Rule refs"
    ],
    ...rows.map((row) => [
      row.id,
      row.apartmentTypeLabel,
      mappingRowKindLabel(row.rowKind),
      row.connectionLabel,
      row.deviceLabel,
      row.roomLabel,
      row.zoneLabel ?? "",
      row.controllerLabel ?? "",
      row.terminationLabel ?? "",
      row.isSpare,
      row.cableImplication ?? "",
      row.commissioningNote,
      row.riskRefs.join(" | "),
      row.sourceRefCount,
      row.ruleTraceRefs.join(" | ")
    ])
  ]
    .map((line) => line.map(csvCell).join(","))
    .join("\r\n");
